import { useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  /**
   * Callback function to load more items
   */
  onLoadMore: () => void;

  /**
   * Whether there are more items to load
   */
  hasMore: boolean;

  /**
   * Whether currently loading
   */
  isLoading: boolean;

  /**
   * Root margin for IntersectionObserver (default: '100px')
   * Triggers loading when sentinel is 100px from viewport
   */
  rootMargin?: string;

  /**
   * Threshold for IntersectionObserver (default: 0.1)
   */
  threshold?: number;
}

/**
 * Custom hook for implementing infinite scroll / lazy loading
 *
 * @example
 * const sentinelRef = useInfiniteScroll({
 *   onLoadMore: () => setPage(prev => prev + 1),
 *   hasMore: hasMore,
 *   isLoading: loading,
 * });
 *
 * return (
 *   <>
 *     {items.map(item => <Item key={item.id} {...item} />)}
 *     <div ref={sentinelRef} /> // Sentinel element
 *   </>
 * );
 */
export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  isLoading,
  rootMargin = '100px',
  threshold = 0.1,
}: UseInfiniteScrollOptions) => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;

      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore]
  );

  // Use callback ref to attach observer when element is mounted
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // If no node, nothing to observe
      if (!node) {
        return;
      }

      // Create new observer and attach to node
      const observer = new IntersectionObserver(handleObserver, {
        root: null, // viewport
        rootMargin,
        threshold,
      });

      observerRef.current = observer;
      observer.observe(node);
    },
    [handleObserver, rootMargin, threshold]
  );

  return sentinelRef;
};
